import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Box,
  CircularProgress,
  Alert,
  Paper,
  Grid,
  Chip,
  Divider,
  Fade,
  Slide,
  Grow,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Avatar
} from '@mui/material';
import { 
  School, 
  Psychology, 
  SportsEsports, 
  Science, 
  Code,
  TrendingUp,
  Lightbulb,
  Close,
  Login,
  PersonAdd,
  BeachAccess,
  Groups,
  Biotech,
  OpenInNew,
  Google
} from '@mui/icons-material';
import './App.css';
import LandingPage from './components/LandingPage';
import PricingPage from './components/PricingPage';
import AboutPage from './components/AboutPage';
import ContactPage from './components/ContactPage';
import NavigationBar from './components/NavigationBar';
import API_BASE_URL from './config';
import ResumePersonalization from './components/ResumePersonalization';
import StudentPlanning from './components/StudentPlanning';
import ResumeBuilder from './components/ResumeBuilder';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import Dashboard from './components/Dashboard';
import ExtracurricularService from './components/ExtracurricularService';
import VolunteeringService from './components/VolunteeringService';
import { UserContext } from './UserContext';
import posthog from 'posthog-js';
import SolutionPage from './components/SolutionPage';
import DemoPage from './components/DemoPage';
import AdminPage from './components/AdminPage';

function usePageTracking() {
  const location = useLocation();
  useEffect(() => {
    posthog.capture('$pageview');
  }, [location]);
}

function App() {
  console.log('App component mounted');
  const [interests, setInterests] = useState('');
  const [location, setLocation] = useState('');
  const [resumeText, setResumeText] = useState('');
  const [resumeFile, setResumeFile] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [user, setUser] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [authTab, setAuthTab] = useState(0);
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [selectedRecommendation, setSelectedRecommendation] = useState(null);
  const [authLoaded, setAuthLoaded] = useState(false);

  // Login form state
  const [loginForm, setLoginForm] = useState({
    email: '',
    password: ''
  });
  
  // Register form state
  const [registerForm, setRegisterForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    grade: '',
    location: '',
    interests: ''
  });

  const navigate = useNavigate();

  // Check for existing token on app load
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    console.debug('Token on app load:', token);
    if (token) {
      verifyToken(token).finally(() => setAuthLoaded(true));
    } else {
      setAuthLoaded(true);
    }
  }, []);

  // Handle OAuth callback
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    
    if (token) {
      // Remove token from URL
      window.history.replaceState({}, document.title, window.location.pathname);
      
      // Store token and verify
      localStorage.setItem('access_token', token);
      verifyToken(token);
      setAuthDialogOpen(false);
    }
  }, []);

  // Listen for OAuth messages from popup
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data.type === 'oauth_success') {
        const token = event.data.token;
        localStorage.setItem('access_token', token);
        verifyToken(token);
        setAuthDialogOpen(false);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  useEffect(() => {
    if (user && subscriptionStatus && !subscriptionStatus.is_subscribed && !user.isAdmin) {
      navigate('/pricing');
    }
  }, [user, subscriptionStatus, navigate]);

  useEffect(() => {
    if (!user && !localStorage.getItem('access_token')) {
      setAuthDialogOpen(true);
    }
  }, [user]);

  const handleGoogleOAuth = () => {
    // Open Google OAuth in a new window
    const oauthWindow = window.open(
      `${API_BASE_URL}/auth/google/login`,
      'google-oauth',
      'width=500,height=600,scrollbars=yes,resizable=yes'
    );

    // Check for OAuth completion
    const checkOAuth = setInterval(() => {
      if (oauthWindow.closed) {
        clearInterval(checkOAuth);
        // Check if we have a token (OAuth callback would have set it)
        const token = localStorage.getItem('access_token');
        if (token) {
          verifyToken(token);
          setAuthDialogOpen(false);
        }
      }
    }, 1000);
  };

  const verifyToken = async (token) => {
    console.log('[VERIFY] Verifying token:', token ? `${token.substring(0, 20)}...` : 'NO TOKEN');
    try {
      const response = await fetch(`${API_BASE_URL}/auth/verify`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('[VERIFY] Response status:', response.status);
      console.log('[VERIFY] Response ok:', response.ok);

      if (response.ok) {
        const data = await response.json();
        console.log('[VERIFY] Token verified successfully, user data:', data);
        console.log('[VERIFY] Subscription status:', data.subscription_status);
        console.log('[VERIFY] Is admin:', data.is_admin);
        setUser({
          id: data.user_id,
          email: data.email,
          fullName: data.full_name,
          profilePicture: data.profile_picture,
          isAdmin: data.is_admin
        });
        setSubscriptionStatus(data.subscription_status);
        console.log('[VERIFY] User and subscription status set');
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        console.error('[VERIFY] Token verification failed:', errorData);
        // Token is invalid, remove it
        localStorage.removeItem('access_token');
        setUser(null);
        setSubscriptionStatus(null);
      }
    } catch (error) {
      console.error('[VERIFY] Token verification error:', error);
      localStorage.removeItem('access_token');
      setUser(null);
      setSubscriptionStatus(null);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    setAuthError('');

    try {
      console.log('[LOGIN] Attempting login for:', loginForm.email);
      console.log('[LOGIN] API URL:', API_BASE_URL);
      
      if (!loginForm.email || !loginForm.password) {
        throw new Error('Please enter both email and password');
      }
      
      const response = await fetch(`${API_BASE_URL}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          username: loginForm.email,
          password: loginForm.password,
        }),
      }).catch((networkError) => {
        console.error('[LOGIN] Network error:', networkError);
        throw new Error(`Cannot connect to server. Please check your internet connection and try again. (${networkError.message})`);
      });

      console.log('[LOGIN] Response status:', response.status);
      console.log('[LOGIN] Response ok:', response.ok);
      console.log('[LOGIN] Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        let errorMessage = `Login failed (Status: ${response.status})`;
        try {
          const errorData = await response.json();
          console.error('[LOGIN] Error response data:', errorData);
          errorMessage = errorData.detail || errorData.message || errorMessage;
        } catch (parseError) {
          console.error('[LOGIN] Could not parse error response:', parseError);
          const text = await response.text().catch(() => '');
          console.error('[LOGIN] Error response text:', text);
          errorMessage = text || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('[LOGIN] Login successful, received data:', { 
        ...data, 
        access_token: data.access_token ? `${data.access_token.substring(0, 20)}...` : 'MISSING' 
      });
      
      if (!data.access_token) {
        throw new Error('No access token received from server. Please try again.');
      }
      
      // Store token in localStorage
      localStorage.setItem('access_token', data.access_token);
      console.log('[LOGIN] Token stored in localStorage');
      
      // Set user and subscription status from login response
      // verifyToken will be called automatically on next render via useEffect
      setUser({
        id: data.user_id,
        email: data.email,
        fullName: data.full_name,
        profilePicture: data.profile_picture,
        isAdmin: data.is_admin
      });
      setSubscriptionStatus(data.subscription_status);
      
      // Verify the token to ensure we have latest data
      console.log('[LOGIN] Verifying token...');
      await verifyToken(data.access_token);
      
      setAuthDialogOpen(false);
      setLoginForm({ email: '', password: '' });
      console.log('[LOGIN] Login flow completed successfully');
      console.log('[LOGIN] Final user state:', { user: data.email, isAdmin: data.is_admin, subscriptionStatus: data.subscription_status });
      navigate('/');
    } catch (err) {
      console.error('[LOGIN] Login error:', err);
      const errorMessage = err.message || 'Login failed. Please check your credentials and try again.';
      setAuthError(errorMessage);
    } finally {
      setAuthLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (registerForm.password !== registerForm.confirmPassword) {
      setAuthError('Passwords do not match');
      return;
    }

    setAuthLoading(true);
    setAuthError('');

    try {
      const response = await fetch(`${API_BASE_URL}/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: registerForm.email,
          password: registerForm.password,
          full_name: registerForm.fullName,
          grade: registerForm.grade,
          location: registerForm.location,
          interests: registerForm.interests
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Registration failed');
      }

      const data = await response.json();
      
      // Store token in localStorage
      localStorage.setItem('access_token', data.access_token);
      
      setUser({
        id: data.id,
        email: data.email,
        fullName: data.full_name,
        profilePicture: data.profile_picture,
        isAdmin: data.is_admin
      });
      setSubscriptionStatus(data.subscription_status);
      setAuthDialogOpen(false);
      setRegisterForm({
        email: '',
        password: '',
        confirmPassword: '',
        fullName: '',
        grade: '',
        location: '',
        interests: ''
      });
      navigate('/');
    } catch (err) {
      setAuthError(err.message);
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = () => {
    setUser(null);
    setSubscriptionStatus(null);
    setRecommendations([]);
    localStorage.removeItem('access_token');
  };

  const handleLogoClick = () => {
    setUser(null);
    setSubscriptionStatus(null);
    setRecommendations([]);
  };

  const handleSubscribe = async (plan) => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        throw new Error('Please log in to continue');
      }

      const response = await fetch(`${API_BASE_URL}/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          subscription_type: plan
        }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          // Token expired, redirect to login
          handleLogout();
          throw new Error('Session expired. Please log in again.');
        } else {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Failed to create subscription');
        }
      }

      const data = await response.json();
      setSubscriptionStatus({
        is_subscribed: true,
        subscription_type: data.subscription_type,
        expires_at: data.expires_at
      });
      return data;
    } catch (error) {
      throw new Error(error.message || 'Failed to process subscription');
    }
  };

  const handleResumeTextChange = (e) => {
    setResumeText(e.target.value);
    setResumeFile(null);
  };

  const handleResumeFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setResumeFile(e.target.files[0]);
      setResumeText('');
    } else {
      setResumeFile(null);
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    setError('');
    setRecommendations([]);

    const formData = new FormData();
    formData.append('interests', interests);
    formData.append('location', location);
    if (resumeFile) {
      formData.append('resume_file', resumeFile);
    } else if (resumeText.trim()) {
      formData.append('resume_text', resumeText);
    }

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/personalize-courses`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to get recommendations');
      }
      const data = await response.json();
      setRecommendations(data.recommendations || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatRecommendations = (data) => {
    if (!data) return [];
    
    // If data is already an array of objects, return it directly
    if (Array.isArray(data)) {
      return data;
    }
    
    // If data is a string (fallback for old format), parse it
    if (typeof data === 'string') {
      const activities = data.split(/\d+\.\s+\*\*Activity Name:\*\*/).filter(Boolean);
      
      return activities.map((activity, index) => {
        const lines = activity.split('\n').filter(line => line.trim());
        const name = lines[0]?.replace(/\*\*/g, '').trim() || '';
        const type = lines.find(line => line.includes('**Type:**'))?.split('**Type:**')[1]?.trim() || '';
        const description = lines.find(line => line.includes('**Description:**'))?.split('**Description:**')[1]?.trim() || '';
        const howToStart = lines.find(line => line.includes('**How to Get Started:**'))?.split('**How to Get Started:**')[1]?.trim() || '';
        
        return { name, type, description, howToStart };
      });
    }
    
    return [];
  };

  const getTypeIcon = (type) => {
    switch (type.toLowerCase()) {
      case 'competition': return <SportsEsports />;
      case 'volunteering': return <Psychology />;
      case 'internship': return <School />;
      case 'online course': return <Code />;
      case 'personal project': return <Science />;
      case 'summer program': return <BeachAccess />;
      case 'club/organization': return <Groups />;
      case 'research': return <Biotech />;
      default: return <School />;
    }
  };

  const getTypeColor = (type) => {
    switch (type.toLowerCase()) {
      case 'competition': return 'primary';
      case 'volunteering': return 'success';
      case 'internship': return 'info';
      case 'online course': return 'warning';
      case 'personal project': return 'secondary';
      case 'summer program': return 'error';
      case 'club/organization': return 'info';
      case 'research': return 'primary';
      default: return 'default';
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty.toLowerCase()) {
      case 'beginner': return 'success';
      case 'intermediate': return 'warning';
      case 'advanced': return 'error';
      default: return 'default';
    }
  };

  const formattedActivities = formatRecommendations(recommendations);

  usePageTracking();

  if (!authLoaded) {
    return <div>Loading...</div>;
  }
  console.debug('authLoaded:', authLoaded, 'user:', user, 'subscriptionStatus:', subscriptionStatus);

  return (
    <UserContext.Provider value={{ user, setUser }}>
      <Box className="App" sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <NavigationBar 
          user={user}
          subscriptionStatus={subscriptionStatus}
          onGetStarted={() => setAuthDialogOpen(true)}
          onPricing={() => navigate('/pricing')}
          onAbout={() => navigate('/about')}
          onContact={() => { console.log('Contact button clicked'); navigate('/contact'); }}
          onLogout={handleLogout}
          onLogin={() => setAuthDialogOpen(true)}
          onLogoClick={handleLogoClick}
        />
        <Routes>
          <Route path="/dashboard" element={
            user && (subscriptionStatus?.is_subscribed || user?.isAdmin)
              ? <Dashboard />
              : <Navigate to="/pricing" />
          } />
          <Route path="/student-planning" element={
            user && (subscriptionStatus?.is_subscribed || user?.isAdmin)
              ? <StudentPlanning />
              : <Navigate to="/pricing" />
          } />
          <Route path="/service/extracurricular" element={
            user && (subscriptionStatus?.is_subscribed || user?.isAdmin)
              ? <ExtracurricularService />
              : <Navigate to="/pricing" />
          } />
          <Route path="/service/volunteering" element={
            user && (subscriptionStatus?.is_subscribed || user?.isAdmin)
              ? <VolunteeringService />
              : <Navigate to="/pricing" />
          } />
          <Route path="/resume-builder" element={
            user && (subscriptionStatus?.is_subscribed || user?.isAdmin)
              ? <ResumeBuilder />
              : <Navigate to="/pricing" />
          } />
          <Route path="/pricing" element={
            <PricingPage 
              onSubscribe={handleSubscribe} 
              onBack={() => navigate('/')} 
            />
          } />
          <Route path="/about" element={<AboutPage onBack={() => navigate('/')} />} />
          <Route path="/contact" element={<ContactPage onBack={() => navigate('/')} />} />
          <Route path="/solution" element={<SolutionPage />} />
          <Route path="/demo" element={<DemoPage />} />
          <Route path="/admin" element={<AdminPage />} />
          {/* Add other routes as needed */}
          {/* Fallback to landing page or 404 */}
          <Route path="*" element={<LandingPage onGetStarted={() => setAuthDialogOpen(true)} onPricing={() => navigate('/pricing')} />} />
        </Routes>

        {/* Authentication Dialog */}
        <Dialog 
          open={authDialogOpen} 
          onClose={() => setAuthDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 4,
              background: 'rgba(255,255,255,0.95)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255,255,255,0.2)'
            }
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {authTab === 0 ? 'Sign In' : 'Create Account'}
              </Typography>
              <IconButton onClick={() => setAuthDialogOpen(false)}>
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          
          <DialogContent>
            <Tabs 
              value={authTab} 
              onChange={(e, newValue) => setAuthTab(newValue)}
              sx={{ mb: 3 }}
            >
              <Tab label="Sign In" icon={<Login />} />
              <Tab label="Register" icon={<PersonAdd />} />
            </Tabs>

            {authError && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                {authError}
              </Alert>
            )}

            {authTab === 0 ? (
              // Login Form
              <form onSubmit={handleLogin}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={loginForm.email}
                  onChange={(e) => setLoginForm({...loginForm, email: e.target.value})}
                  sx={{ mb: 3 }}
                  required
                />
                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({...loginForm, password: e.target.value})}
                  sx={{ mb: 3 }}
                  required
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={authLoading}
                  startIcon={authLoading ? <CircularProgress size={20} /> : <Login />}
                  sx={{ 
                    height: 48,
                    borderRadius: 2,
                    background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #5a6fd8 30%, #6a4190 90%)'
                    }
                  }}
                >
                  {authLoading ? 'Signing In...' : 'Sign In'}
                </Button>
                
                <Divider sx={{ my: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    or
                  </Typography>
                </Divider>
                
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleGoogleOAuth}
                  startIcon={<Google />}
                  sx={{ 
                    height: 48,
                    borderRadius: 2,
                    borderColor: '#4285f4',
                    color: '#4285f4',
                    '&:hover': {
                      borderColor: '#3367d6',
                      backgroundColor: 'rgba(66, 133, 244, 0.04)'
                    }
                  }}
                >
                  Sign in with Google
                </Button>
              </form>
            ) : (
              // Register Form
              <form onSubmit={handleRegister}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Full Name"
                      value={registerForm.fullName}
                      onChange={(e) => setRegisterForm({...registerForm, fullName: e.target.value})}
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Email"
                      type="email"
                      value={registerForm.email}
                      onChange={(e) => setRegisterForm({...registerForm, email: e.target.value})}
                      required
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Grade"
                      value={registerForm.grade}
                      onChange={(e) => setRegisterForm({...registerForm, grade: e.target.value})}
                      placeholder="e.g., 10th, 11th"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Location"
                      value={registerForm.location}
                      onChange={(e) => setRegisterForm({...registerForm, location: e.target.value})}
                      placeholder="City, State"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Interests"
                      multiline
                      rows={2}
                      value={registerForm.interests}
                      onChange={(e) => setRegisterForm({...registerForm, interests: e.target.value})}
                      placeholder="e.g., computer science, robotics, music"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Password"
                      type="password"
                      value={registerForm.password}
                      onChange={(e) => setRegisterForm({...registerForm, password: e.target.value})}
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Confirm Password"
                      type="password"
                      value={registerForm.confirmPassword}
                      onChange={(e) => setRegisterForm({...registerForm, confirmPassword: e.target.value})}
                      required
                    />
                  </Grid>
                </Grid>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={authLoading}
                  startIcon={authLoading ? <CircularProgress size={20} /> : <PersonAdd />}
                  sx={{ 
                    mt: 3,
                    height: 48,
                    borderRadius: 2,
                    background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #5a6fd8 30%, #6a4190 90%)'
                    }
                  }}
                >
                  {authLoading ? 'Creating Account...' : 'Create Account'}
                </Button>
                
                <Divider sx={{ my: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    or
                  </Typography>
                </Divider>
                
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleGoogleOAuth}
                  startIcon={<Google />}
                  sx={{ 
                    height: 48,
                    borderRadius: 2,
                    borderColor: '#4285f4',
                    color: '#4285f4',
                    '&:hover': {
                      borderColor: '#3367d6',
                      backgroundColor: 'rgba(66, 133, 244, 0.04)'
                    }
                  }}
                >
                  Sign up with Google
                </Button>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* Opportunity Detail Modal */}
        <Dialog 
          open={!!selectedOpportunity} 
          onClose={() => setSelectedOpportunity(null)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 4,
              background: 'rgba(255,255,255,0.95)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255,255,255,0.2)'
            }
          }}
        >
          {selectedOpportunity && (
            <>
              <DialogTitle sx={{ pb: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box display="flex" alignItems="center">
                    <Box 
                      sx={{ 
                        p: 1.5, 
                        borderRadius: 2, 
                        bgcolor: `${getTypeColor(selectedOpportunity.type)}.light`,
                        color: `${getTypeColor(selectedOpportunity.type)}.main`,
                        mr: 2
                      }}
                    >
                      {getTypeIcon(selectedOpportunity.type)}
                    </Box>
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        {selectedOpportunity.name}
                      </Typography>
                      <Box display="flex" gap={1} mt={1}>
                        <Chip
                          label={selectedOpportunity.type}
                          color={getTypeColor(selectedOpportunity.type)}
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          label={selectedOpportunity.difficulty}
                          color={getDifficultyColor(selectedOpportunity.difficulty)}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                    </Box>
                  </Box>
                  <IconButton onClick={() => setSelectedOpportunity(null)}>
                    <Close />
                  </IconButton>
                </Box>
              </DialogTitle>
              
              <DialogContent>
                <Typography 
                  variant="body1" 
                  color="text.secondary" 
                  paragraph 
                  sx={{ 
                    lineHeight: 1.7,
                    mb: 3
                  }}
                >
                  {selectedOpportunity.description}
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                      📍 Location
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {selectedOpportunity.location}
                    </Typography>
                    
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                      ⏱️ Duration
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {selectedOpportunity.duration}
                    </Typography>
                    
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                      👥 Eligibility
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {selectedOpportunity.eligibility}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                      💰 Cost
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {selectedOpportunity.cost}
                    </Typography>
                    
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                      📅 Application Deadline
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {selectedOpportunity.application_deadline}
                    </Typography>
                    
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                      🎯 Benefits
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {selectedOpportunity.benefits}
                    </Typography>
                  </Grid>
                </Grid>
                
                <Divider sx={{ my: 3 }} />
                
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                  📞 Contact Information
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  {selectedOpportunity.contact_info}
                </Typography>
                
                {selectedOpportunity.website && (
                  <Box sx={{ mt: 2 }}>
                    <Button
                      variant="contained"
                      href={selectedOpportunity.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      startIcon={<OpenInNew />}
                      sx={{ 
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 500
                      }}
                    >
                      Visit Website
                    </Button>
                  </Box>
                )}
              </DialogContent>
            </>
          )}
        </Dialog>
      </Box>
    </UserContext.Provider>
  );
}

export default App;
